<template>
  <el-dialog
    title="售后客服"
    :visible.sync="dialogVisible"
    width="720px"
    top="8vh"
    :close-on-click-modal="false"
    append-to-body
    custom-class="klgeek-cs-dialog"
    @closed="onClosed"
  >
    <div class="cs-chat">
      <div class="cs-toolbar">
        <p class="sub">可查询订单、申请退款</p>
        <el-button size="mini" @click="onClear">新会话</el-button>
      </div>
      <div ref="scrollBox" class="cs-messages">
        <div
          v-for="(m, idx) in messages"
          :key="idx"
          class="msg-row"
          :class="m.role"
        >
          <div class="bubble" v-html="m.role === 'user' ? escapeHtml(m.content) : renderMd(m.content)"></div>
        </div>
        <div v-if="toolHint" class="tool-hint">{{ toolHint }}</div>
      </div>
      <div class="cs-composer">
        <el-input
          v-model="inputText"
          type="textarea"
          :rows="3"
          placeholder="描述您的问题，如：查订单、申请退款…"
          :disabled="streaming"
          @keydown.native.ctrl.enter="onSend"
        />
        <el-button type="primary" :loading="streaming" :disabled="!canSend" @click="onSend">发送</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  syncChat,
  streamChat,
  clearMemory,
  getCsConversationId,
  setCsConversationId,
  clearCsConversationId,
} from '../../api/klgeekCs'

export default {
  name: 'KlgeekCsChat',
  props: {
    visible: {
      type: Boolean,
      default: undefined,
    },
    orderNo: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      internalVisible: false,
      inputText: '',
      streaming: false,
      toolHint: '',
      conversationId: getCsConversationId(),
      messages: [
        {
          role: 'assistant',
          content: '您好，我是知识极客售后客服。您可以查询订单状态或申请退款。',
        },
      ],
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible !== undefined ? this.visible : this.internalVisible
      },
      set(val) {
        if (this.visible !== undefined) {
          this.$emit('update:visible', val)
        } else {
          this.internalVisible = val
        }
      },
    },
    canSend() {
      return (this.inputText || '').trim().length > 0 && !this.streaming
    },
  },
  watch: {
    visible(val) {
      if (val) {
        this.applyOrderNoHint()
      }
    },
    orderNo() {
      if (this.dialogVisible) {
        this.applyOrderNoHint()
      }
    },
  },
  mounted() {
    if (this.visible === undefined) {
      this.internalVisible = true
    }
    if (this.dialogVisible) {
      this.applyOrderNoHint()
    }
  },
  methods: {
    applyOrderNoHint() {
      const no = (this.orderNo || '').trim()
      if (no) {
        this.inputText = `我想咨询订单 ${no}`
      }
    },
    escapeHtml(text) {
      return String(text || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>')
    },
    renderMd(text) {
      return this.escapeHtml(text)
    },
    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scrollBox
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    async onSend() {
      const text = (this.inputText || '').trim()
      if (!text || this.streaming) return
      this.messages.push({ role: 'user', content: text })
      this.messages.push({ role: 'assistant', content: '' })
      this.inputText = ''
      this.streaming = true
      this.toolHint = ''
      this.scrollBottom()
      const last = () => this.messages[this.messages.length - 1]
      try {
        await streamChat(
          { message: text, conversationId: this.conversationId },
          {
            onConversationId: (id) => {
              this.conversationId = id
              setCsConversationId(id)
            },
            onTool: () => {
              this.toolHint = '正在查询订单信息…'
            },
            onDelta: (delta) => {
              this.toolHint = ''
              if (last().role === 'assistant') {
                last().content += delta
                this.scrollBottom()
              }
            },
            onError: (msg) => {
              if (last().role === 'assistant') last().content = msg
            },
          }
        )
      } catch {
        try {
          const data = await syncChat({ message: text, conversationId: this.conversationId })
          if (data.conversationId) {
            this.conversationId = data.conversationId
            setCsConversationId(data.conversationId)
          }
          if (last().role === 'assistant') last().content = data.message || ''
        } catch (e) {
          if (last().role === 'assistant') last().content = e.message || '请求失败'
        }
      } finally {
        this.streaming = false
        this.toolHint = ''
        this.scrollBottom()
      }
    },
    async onClear() {
      if (this.conversationId) {
        try {
          await clearMemory(this.conversationId)
        } catch {
          /* ignore */
        }
      }
      clearCsConversationId()
      this.conversationId = ''
      this.messages = [{ role: 'assistant', content: '已开始新会话。请问有什么可以帮您？' }]
    },
    onClosed() {
      this.$emit('close')
      if (this.visible === undefined && this.$router) {
        this.$router.back()
      }
    },
  },
}
</script>

<style scoped>
.cs-chat {
  display: flex;
  flex-direction: column;
  height: 65vh;
  min-height: 420px;
  background: #f5f6fa;
  border-radius: 8px;
  overflow: hidden;
}
.cs-toolbar {
  padding: 0 4px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
}
.sub {
  margin: 0;
  flex: 1;
  color: #888;
  font-size: 13px;
}
.cs-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px 4px;
}
.msg-row {
  display: flex;
  margin-bottom: 12px;
}
.msg-row.user {
  justify-content: flex-end;
}
.msg-row.user .bubble {
  background: #587ff0;
  color: #fff;
}
.msg-row.assistant .bubble {
  background: #fff;
  color: #333;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
}
.bubble {
  max-width: 75%;
  padding: 10px 14px;
  border-radius: 12px;
  line-height: 1.5;
  word-break: break-word;
}
.tool-hint {
  color: #888;
  font-size: 13px;
  padding: 8px 0;
}
.cs-composer {
  padding-top: 12px;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>

<style>
.klgeek-cs-dialog .el-dialog__body {
  padding-top: 8px;
}
</style>
